import clsx from 'clsx';
import { useState, type JSX, type ReactNode } from 'react';
import type { ContactSectionLabels } from '@/lib/types/siteSettings';
import { PublicPageHeading } from './PublicPageHeading';

interface ContactHour {
  label: string;
  value: string;
}

interface LeadershipContact {
  name: string;
  title?: string;
}

interface ContactSectionProps {
  heading: string;
  description?: ReactNode;
  addressLines: string[];
  phone: string;
  email: string;
  hours: ContactHour[];
  contacts: LeadershipContact[];
  mapEmbedUrl?: string;
  mapTitle?: string;
  labels?: ContactSectionLabels;
  className?: string;
}

export function ContactSection({
  heading,
  description,
  addressLines,
  phone,
  email,
  hours,
  contacts,
  mapEmbedUrl,
  mapTitle,
  labels,
  className,
}: ContactSectionProps): JSX.Element {
  const headingId = 'contact-section-heading';
  const hasMap = typeof mapEmbedUrl === 'string' && mapEmbedUrl.length > 0;
  const mapAddress = addressLines.filter((line) => line.trim().length > 0).join(', ');
  const mapLink =
    mapAddress.length > 0
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`
      : 'https://maps.google.com';
  const [mapLoaded, setMapLoaded] = useState(false);
  const resolveLabel = (value: string | undefined, fallback: string) =>
    typeof value === 'string' && value.trim().length > 0 ? value : fallback;
  const resolvedLabels = {
    addressLabel: resolveLabel(labels?.addressLabel, 'Address'),
    phoneLabel: resolveLabel(labels?.phoneLabel, 'Phone'),
    emailLabel: resolveLabel(labels?.emailLabel, 'Email'),
    hoursLabel: resolveLabel(labels?.hoursLabel, 'Hours'),
    leadershipLabel: resolveLabel(labels?.leadershipLabel, 'Leadership Contacts'),
    mapTitle: resolveLabel(labels?.mapTitle, 'Map location'),
    mapPreviewTitle: resolveLabel(labels?.mapPreviewTitle, 'Map preview'),
    mapPreviewDescription: resolveLabel(
      labels?.mapPreviewDescription,
      'Use the link below for turn-by-turn directions.',
    ),
    mapFooter: resolveLabel(
      labels?.mapFooter,
      'View this location on the map for turn-by-turn directions.',
    ),
    mapLinkLabel: resolveLabel(labels?.mapLinkLabel, 'Open in maps'),
  };
  const resolvedMapTitle = resolveLabel(mapTitle, resolvedLabels.mapTitle);

  return (
    <section
      aria-labelledby={headingId}
      className={clsx('flex flex-col gap-8 md:gap-10', className)}
      data-testid="contact-section"
    >
      <PublicPageHeading
        title={heading}
        description={description}
        layout="split"
        contentClassName="max-w-3xl"
        className="gap-6"
      />

      <div className={clsx('grid gap-6', hasMap ? 'lg:grid-cols-2' : 'lg:grid-cols-1')}>
        <div className="rounded-3xl bg-white p-6 shadow-card ring-1 ring-surface-subtle">
          <dl className="grid gap-6 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {resolvedLabels.addressLabel}
              </dt>
              <dd className="mt-2 text-base font-medium text-text-primary">
                {addressLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </dd>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  {resolvedLabels.phoneLabel}
                </dt>
                <dd className="mt-2 text-base font-medium text-brand-primary">
                  <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="hover:underline">
                    {phone}
                  </a>
                </dd>
              </div>

              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  {resolvedLabels.emailLabel}
                </dt>
                <dd className="mt-2 text-base font-medium text-brand-primary">
                  <a href={`mailto:${email}`} className="hover:underline">
                    {email}
                  </a>
                </dd>
              </div>
            </div>

            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {resolvedLabels.hoursLabel}
              </dt>
              <dd className="mt-2 flex flex-col gap-1 text-base text-text-primary">
                {hours.map((entry) => (
                  <div key={`${entry.label}-${entry.value}`} className="flex flex-col">
                    <span className="font-medium text-text-primary/80">{entry.label}</span>
                    <span>{entry.value}</span>
                  </div>
                ))}
              </dd>
            </div>

            <div className="rounded-2xl bg-surface-cream p-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {resolvedLabels.leadershipLabel}
              </dt>
              <dd className="mt-3 space-y-2 text-base text-text-primary">
                {contacts.map((contact) => {
                  const hasTitle = typeof contact.title === 'string' && contact.title.length > 0;

                  return (
                    <div key={contact.name} className="flex flex-col">
                      <span className="font-semibold text-text-primary">{contact.name}</span>
                      {hasTitle ? (
                        <span className="text-sm text-text-primary/70">{contact.title}</span>
                      ) : null}
                    </div>
                  );
                })}
              </dd>
            </div>
          </dl>
        </div>

        {hasMap ? (
          <div className="overflow-hidden rounded-3xl bg-white shadow-card ring-1 ring-surface-subtle">
            <div className="relative aspect-[4/3] w-full bg-surface-cream">
              <iframe
                title={resolvedMapTitle}
                src={mapEmbedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className={clsx(
                  'h-full w-full border-0 transition-opacity duration-300',
                  mapLoaded ? 'opacity-100' : 'opacity-0',
                )}
                allowFullScreen
                onLoad={() => {
                  setMapLoaded(true);
                }}
              />
              <div
                className={clsx(
                  'pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-sm text-text-primary/70 transition-opacity duration-300',
                  mapLoaded ? 'opacity-0' : 'opacity-100',
                )}
              >
                <span className="text-base font-semibold text-text-primary">
                  {resolvedLabels.mapPreviewTitle}
                </span>
                <span>{resolvedLabels.mapPreviewDescription}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-surface-subtle px-5 py-4 text-sm text-text-primary/75">
              <span>{resolvedLabels.mapFooter}</span>
              <a
                href={mapLink}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-brand-primary hover:underline"
              >
                {resolvedLabels.mapLinkLabel}
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
